import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  LayoutDashboard, Truck, Users, Building2, ClipboardList, Route as RouteIcon, Plus, Pencil, Archive, ArchiveRestore, Power, UserCog, IndianRupee, BarChart3,
} from "lucide-react";
import { StatusBadge, KpiGrid, AssignDialog, TabBtn, fmtMoney } from "@/components/transport/transportShared";
import { PaymentsTab, ReportsTab } from "@/components/transport/FinanceTabs";

const TRIP_STATUSES = ["ASSIGNED", "LOADING", "FULL", "DEPARTED", "AT_PASHU_HAAT", "COMPLETED"];

/* ---------- generic helpers ---------- */
function useTransportData() {
  const [data, setData] = useState({ vehicles: [], drivers: [], transporters: [], requests: [], kpi: null });
  const refresh = useCallback(() => {
    Promise.all([
      api.get("/transport/vehicles", { params: { include_archived: true } }),
      api.get("/transport/drivers", { params: { include_archived: true } }),
      api.get("/transport/transporters", { params: { include_archived: true } }),
      api.get("/transport/requests"),
      api.get("/transport/dashboard"),
    ]).then(([v, d, t, r, k]) => setData({ vehicles: v.data, drivers: d.data, transporters: t.data, requests: r.data, kpi: k.data }))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load transport data"));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { ...data, refresh };
}

function Table({ cols, children, testId }) {
  return (
    <div className="am-card overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
            {cols.map((c) => <th key={c} className="px-3 py-3 whitespace-nowrap">{c}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function FormDialog({ open, setOpen, title, fields, onSave, busy }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="tr-form-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{fields}</div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={busy} data-testid="tr-form-save" onClick={onSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</Label><div className="mt-1">{children}</div></div>
);

/* ---------- VEHICLES TAB ---------- */
function VehiclesTab({ vehicles, drivers, transporters, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // vehicle doc or null (= add)
  const [form, setForm] = useState({});
  const [driverFor, setDriverFor] = useState(null); // vehicle for driver assignment
  const [driverId, setDriverId] = useState("");
  const [busy, setBusy] = useState(false);
  const driverName = (id) => drivers.find((d) => d.driver_id === id)?.name || "—";

  const openAdd = () => { setEditing(null); setForm({ vehicle_type: "truck", animal_capacity: 10 }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm(v); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { registration_number, vehicle_type, operating_area, animal_capacity, current_location } = form;
        await api.patch(`/transport/vehicles/${editing.vehicle_id}`,
          { registration_number, vehicle_type, operating_area, animal_capacity: Number(animal_capacity), current_location });
      } else {
        await api.post("/transport/vehicles", { ...form, animal_capacity: Number(form.animal_capacity) });
      }
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveDriver = () => act(async () => {
    await api.patch(`/transport/vehicles/${driverFor.vehicle_id}`, { driver_id: driverId });
    setDriverFor(null);
  }, "Driver assigned");

  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="tr-add-vehicle-btn" onClick={openAdd}><Plus className="w-4 h-4 mr-1" /> Add Vehicle</Button>
      <Table testId="tr-vehicles-table" cols={["Vehicle", "Driver", "Area", "Capacity", "Loaded", "Remaining", "Status", "Actions"]}>
        {vehicles.map((v) => (
          <tr key={v.vehicle_id} className={`border-b last:border-0 ${v.is_archived ? "opacity-50" : ""}`} data-testid={`tr-vehicle-row-${v.vehicle_id}`}>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{v.registration_number}<div className="text-xs font-normal text-muted-foreground">{v.vehicle_type}{v.is_archived ? " · archived" : !v.is_active ? " · inactive" : ""}</div></td>
            <td className="px-3 py-3 whitespace-nowrap">{driverName(v.driver_id)}</td>
            <td className="px-3 py-3 whitespace-nowrap">{v.operating_area}</td>
            <td className="px-3 py-3">{v.animal_capacity}</td>
            <td className="px-3 py-3" data-testid={`tr-loaded-${v.vehicle_id}`}>{v.loaded_animals}</td>
            <td className="px-3 py-3 font-bold" data-testid={`tr-remaining-${v.vehicle_id}`}>{v.remaining_capacity}</td>
            <td className="px-3 py-3"><StatusBadge status={v.status} /></td>
            <td className="px-3 py-3">
              <div className="flex gap-1 justify-end flex-wrap">
                <Button size="icon" variant="ghost" title="Edit" data-testid={`tr-edit-${v.vehicle_id}`} onClick={() => openEdit(v)}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" title="Assign / change driver" data-testid={`tr-driver-${v.vehicle_id}`} onClick={() => { setDriverFor(v); setDriverId(v.driver_id || ""); }}><UserCog className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" title={v.is_active ? "Deactivate" : "Activate"} data-testid={`tr-toggle-${v.vehicle_id}`}
                  onClick={() => act(() => api.patch(`/transport/vehicles/${v.vehicle_id}`, { is_active: !v.is_active }), v.is_active ? "Deactivated" : "Activated")}>
                  <Power className={`w-4 h-4 ${v.is_active ? "text-emerald-600" : "text-muted-foreground"}`} />
                </Button>
                {!v.is_archived
                  ? <Button size="icon" variant="ghost" title="Archive" data-testid={`tr-archive-${v.vehicle_id}`} onClick={() => act(() => api.post(`/transport/vehicles/${v.vehicle_id}/archive`), "Archived")}><Archive className="w-4 h-4 text-destructive" /></Button>
                  : <Button size="icon" variant="ghost" title="Restore" data-testid={`tr-restore-${v.vehicle_id}`} onClick={() => act(() => api.post(`/transport/vehicles/${v.vehicle_id}/restore`), "Restored")}><ArchiveRestore className="w-4 h-4 text-primary" /></Button>}
              </div>
            </td>
          </tr>
        ))}
      </Table>

      <FormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} title={editing ? "Edit Vehicle" : "Add Vehicle"}
        fields={<>
          <F label="Registration Number"><Input className="am-input" data-testid="tr-f-reg" value={form.registration_number || ""} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} /></F>
          <F label="Vehicle Type"><Input className="am-input" data-testid="tr-f-type" value={form.vehicle_type || ""} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} /></F>
          {!editing && (
            <F label="Transporter">
              <Select value={form.transporter_id || ""} onValueChange={(v) => setForm({ ...form, transporter_id: v })}>
                <SelectTrigger className="am-input" data-testid="tr-f-transporter"><SelectValue placeholder="Select transporter" /></SelectTrigger>
                <SelectContent>{transporters.filter((t) => !t.is_archived).map((t) => <SelectItem key={t.transporter_id} value={t.transporter_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          )}
          <F label="Operating Area"><Input className="am-input" data-testid="tr-f-area" value={form.operating_area || ""} onChange={(e) => setForm({ ...form, operating_area: e.target.value })} /></F>
          <F label="Animal Capacity"><Input className="am-input" type="number" min={1} data-testid="tr-f-capacity" value={form.animal_capacity || ""} onChange={(e) => setForm({ ...form, animal_capacity: e.target.value })} /></F>
        </>} />

      <FormDialog open={!!driverFor} setOpen={() => setDriverFor(null)} busy={busy} onSave={saveDriver} title={`Driver for ${driverFor?.registration_number || ""}`}
        fields={
          <F label="Driver">
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger className="am-input" data-testid="tr-f-driver"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>{drivers.filter((d) => !d.is_archived && d.is_active).map((d) => <SelectItem key={d.driver_id} value={d.driver_id}>{d.name} ({d.mobile})</SelectItem>)}</SelectContent>
            </Select>
          </F>
        } />
    </div>
  );
}

/* ---------- DRIVERS TAB ---------- */
function DriversTab({ drivers, vehicles, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const vehicleReg = (id) => vehicles.find((v) => v.vehicle_id === id)?.registration_number || "—";
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/transport/drivers", form);
      toast.success("Driver added (login via existing OTP system)");
      setOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="tr-add-driver-btn" onClick={() => { setForm({}); setOpen(true); }}><Plus className="w-4 h-4 mr-1" /> Add Driver</Button>
      <Table testId="tr-drivers-table" cols={["Driver", "Mobile", "Vehicle", "Area", "Status", "Actions"]}>
        {drivers.map((d) => (
          <tr key={d.driver_id} className={`border-b last:border-0 ${d.is_archived ? "opacity-50" : ""}`}>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{d.name}{d.is_archived ? <span className="text-xs font-normal text-muted-foreground"> · archived</span> : null}</td>
            <td className="px-3 py-3 font-mono text-xs">{d.mobile}</td>
            <td className="px-3 py-3 whitespace-nowrap">{vehicleReg(d.vehicle_id)}</td>
            <td className="px-3 py-3">{d.operating_area || "—"}</td>
            <td className="px-3 py-3"><StatusBadge status={d.status} /></td>
            <td className="px-3 py-3">
              <div className="flex gap-1 justify-end">
                <Button size="icon" variant="ghost" title={d.is_active ? "Deactivate" : "Activate"}
                  onClick={() => act(() => api.patch(`/transport/drivers/${d.driver_id}`, { is_active: !d.is_active }), d.is_active ? "Deactivated" : "Activated")}>
                  <Power className={`w-4 h-4 ${d.is_active ? "text-emerald-600" : "text-muted-foreground"}`} />
                </Button>
                {!d.is_archived
                  ? <Button size="icon" variant="ghost" title="Archive" onClick={() => act(() => api.post(`/transport/drivers/${d.driver_id}/archive`), "Archived")}><Archive className="w-4 h-4 text-destructive" /></Button>
                  : <Button size="icon" variant="ghost" title="Restore" onClick={() => act(() => api.post(`/transport/drivers/${d.driver_id}/restore`), "Restored")}><ArchiveRestore className="w-4 h-4 text-primary" /></Button>}
              </div>
            </td>
          </tr>
        ))}
      </Table>
      <FormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} title="Add Driver"
        fields={<>
          <F label="Name"><Input className="am-input" data-testid="tr-f-driver-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
          <F label="Mobile (single-login user)"><Input className="am-input" data-testid="tr-f-driver-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></F>
          <F label="Operating Area"><Input className="am-input" data-testid="tr-f-driver-area" value={form.operating_area || ""} onChange={(e) => setForm({ ...form, operating_area: e.target.value })} /></F>
        </>} />
    </div>
  );
}

/* ---------- TRANSPORTERS TAB ---------- */
function TransportersTab({ transporters, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, operating_areas: (form.areas_text || "").split(",").map((s) => s.trim()).filter(Boolean) };
      delete body.areas_text;
      await api.post("/transport/transporters", body);
      toast.success("Transporter added"); setOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="tr-add-transporter-btn" onClick={() => { setForm({}); setOpen(true); }}><Plus className="w-4 h-4 mr-1" /> Add Transporter</Button>
      <Table testId="tr-transporters-table" cols={["Name", "Business", "Mobile", "Operating Areas", "Status", "Actions"]}>
        {transporters.map((t) => (
          <tr key={t.transporter_id} className={`border-b last:border-0 ${t.is_archived ? "opacity-50" : ""}`}>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{t.name}</td>
            <td className="px-3 py-3">{t.business_name || "—"}</td>
            <td className="px-3 py-3 font-mono text-xs">{t.mobile}</td>
            <td className="px-3 py-3">{(t.operating_areas || []).join(", ") || "—"}</td>
            <td className="px-3 py-3"><StatusBadge status={t.is_archived ? "OFFLINE" : t.status === "ACTIVE" ? "AVAILABLE" : "OFFLINE"} /></td>
            <td className="px-3 py-3 text-right">
              {!t.is_archived
                ? <Button size="icon" variant="ghost" title="Archive" onClick={() => act(() => api.post(`/transport/transporters/${t.transporter_id}/archive`), "Archived")}><Archive className="w-4 h-4 text-destructive" /></Button>
                : <Button size="icon" variant="ghost" title="Restore" onClick={() => act(() => api.post(`/transport/transporters/${t.transporter_id}/restore`), "Restored")}><ArchiveRestore className="w-4 h-4 text-primary" /></Button>}
            </td>
          </tr>
        ))}
      </Table>
      <FormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} title="Add Transporter"
        fields={<>
          <F label="Name"><Input className="am-input" data-testid="tr-f-t-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
          <F label="Business Name"><Input className="am-input" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></F>
          <F label="Mobile"><Input className="am-input" data-testid="tr-f-t-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></F>
          <F label="Operating Areas (comma separated)"><Input className="am-input" data-testid="tr-f-t-areas" value={form.areas_text || ""} onChange={(e) => setForm({ ...form, areas_text: e.target.value })} /></F>
        </>} />
    </div>
  );
}

/* ---------- REQUESTS / TRIPS TABS ---------- */
function RequestsTable({ requests, vehicles, drivers, refresh, withActions }) {
  const [assignFor, setAssignFor] = useState(null);
  const reg = (id) => vehicles.find((v) => v.vehicle_id === id)?.registration_number || "—";
  const drv = (id) => drivers.find((d) => d.driver_id === id)?.name || "—";
  const cancel = async (r) => {
    try { await api.patch(`/transport/requests/${r.request_id}`, { status: "CANCELLED" }); toast.success("Request cancelled"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <Table testId="tr-requests-table" cols={["Request", "Pickup", "Destination", "Animals", "Needs", "Fare", "Status", "Vehicle", "Driver", ...(withActions ? ["Actions"] : [])]}>
        {requests.map((r) => (
          <tr key={r.request_id} className="border-b last:border-0" data-testid={`tr-request-row-${r.request_id}`}>
            <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{r.request_id.slice(0, 8)}<div className="font-sans font-bold text-sm">{r.customer_name}</div></td>
            <td className="px-3 py-3 whitespace-nowrap">{r.pickup_area}{r.pickup_location ? <div className="text-xs text-muted-foreground">{r.pickup_location}</div> : null}</td>
            <td className="px-3 py-3 whitespace-nowrap">{r.destination_pashu_haat}</td>
            <td className="px-3 py-3 whitespace-nowrap">{r.animal_quantity} {r.animal_type}</td>
            <td className="px-3 py-3">{r.required_capacity}</td>
            <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(r.transport_fare)}</td>
            <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
            <td className="px-3 py-3 whitespace-nowrap" data-testid={`tr-req-vehicle-${r.request_id}`}>{reg(r.assigned_vehicle_id)}</td>
            <td className="px-3 py-3 whitespace-nowrap">{drv(r.assigned_driver_id)}</td>
            {withActions && (
              <td className="px-3 py-3">
                <div className="flex gap-1.5 justify-end">
                  {["PENDING", "MATCHING", "ASSIGNED"].includes(r.status) && (
                    <Button size="sm" className="am-btn-primary rounded-full text-xs" data-testid={`tr-assign-btn-${r.request_id}`} onClick={() => setAssignFor(r)}>
                      {r.assigned_vehicle_id ? "Reassign" : "Assign"}
                    </Button>
                  )}
                  {["PENDING", "MATCHING"].includes(r.status) && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => cancel(r)}>Cancel</Button>
                  )}
                </div>
              </td>
            )}
          </tr>
        ))}
      </Table>
      <AssignDialog request={assignFor} open={!!assignFor} setOpen={() => setAssignFor(null)} onDone={refresh} />
    </>
  );
}

function RequestsTab({ requests, vehicles, drivers, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/transport/requests", { ...form, animal_quantity: Number(form.animal_quantity), transport_fare: Number(form.transport_fare || 0) });
      toast.success("Request created"); setOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="tr-add-request-btn" onClick={() => { setForm({ animal_type: "buffalo" }); setOpen(true); }}><Plus className="w-4 h-4 mr-1" /> New Request</Button>
      <RequestsTable requests={requests} vehicles={vehicles} drivers={drivers} refresh={refresh} withActions />
      <FormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} title="New Transport Request"
        fields={<>
          <F label="Customer Name"><Input className="am-input" data-testid="tr-f-r-customer" value={form.customer_name || ""} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Pickup Area"><Input className="am-input" data-testid="tr-f-r-area" value={form.pickup_area || ""} onChange={(e) => setForm({ ...form, pickup_area: e.target.value })} /></F>
            <F label="Pickup Location"><Input className="am-input" value={form.pickup_location || ""} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} /></F>
          </div>
          <F label="Destination Pashu Haat"><Input className="am-input" data-testid="tr-f-r-dest" value={form.destination_pashu_haat || ""} onChange={(e) => setForm({ ...form, destination_pashu_haat: e.target.value })} /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Animal Type"><Input className="am-input" value={form.animal_type || ""} onChange={(e) => setForm({ ...form, animal_type: e.target.value })} /></F>
            <F label="Quantity"><Input className="am-input" type="number" min={1} data-testid="tr-f-r-qty" value={form.animal_quantity || ""} onChange={(e) => setForm({ ...form, animal_quantity: e.target.value })} /></F>
            <F label="Fare ₹"><Input className="am-input" type="number" min={0} value={form.transport_fare || ""} onChange={(e) => setForm({ ...form, transport_fare: e.target.value })} /></F>
          </div>
        </>} />
    </div>
  );
}

/* ---------- PAGE ---------- */
export default function AdminTransport() {
  const [tab, setTab] = useState("dashboard");
  const data = useTransportData();
  const live = {
    vehicles: data.vehicles.filter((v) => !v.is_archived),
    trips: data.requests.filter((r) => TRIP_STATUSES.includes(r.status)),
  };
  return (
    <DashboardShell title="Transport Management" subtitle="Vehicles, drivers, transporters & trips">
      <div className="flex gap-2 flex-wrap mb-6">
        <TabBtn testId="tr-tab-dashboard" active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={LayoutDashboard}>Transport Dashboard</TabBtn>
        <TabBtn testId="tr-tab-vehicles" active={tab === "vehicles"} onClick={() => setTab("vehicles")} icon={Truck}>Vehicles</TabBtn>
        <TabBtn testId="tr-tab-drivers" active={tab === "drivers"} onClick={() => setTab("drivers")} icon={Users}>Drivers</TabBtn>
        <TabBtn testId="tr-tab-transporters" active={tab === "transporters"} onClick={() => setTab("transporters")} icon={Building2}>Transporters</TabBtn>
        <TabBtn testId="tr-tab-requests" active={tab === "requests"} onClick={() => setTab("requests")} icon={ClipboardList}>Transport Requests</TabBtn>
        <TabBtn testId="tr-tab-trips" active={tab === "trips"} onClick={() => setTab("trips")} icon={RouteIcon}>Trips</TabBtn>
        <TabBtn testId="tr-tab-payments" active={tab === "payments"} onClick={() => setTab("payments")} icon={IndianRupee}>Payments</TabBtn>
        <TabBtn testId="tr-tab-reports" active={tab === "reports"} onClick={() => setTab("reports")} icon={BarChart3}>Reports</TabBtn>
      </div>
      {tab === "dashboard" && <KpiGrid data={data.kpi} />}
      {tab === "vehicles" && <VehiclesTab vehicles={data.vehicles} drivers={data.drivers} transporters={data.transporters} refresh={data.refresh} />}
      {tab === "drivers" && <DriversTab drivers={data.drivers} vehicles={live.vehicles} refresh={data.refresh} />}
      {tab === "transporters" && <TransportersTab transporters={data.transporters} refresh={data.refresh} />}
      {tab === "requests" && <RequestsTab requests={data.requests} vehicles={live.vehicles} drivers={data.drivers} refresh={data.refresh} />}
      {tab === "trips" && <RequestsTable requests={live.trips} vehicles={live.vehicles} drivers={data.drivers} refresh={data.refresh} />}
      {tab === "payments" && <PaymentsTab vehicles={data.vehicles} drivers={data.drivers} transporters={data.transporters} />}
      {tab === "reports" && <ReportsTab />}
    </DashboardShell>
  );
}
